import React, { lazy, Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';

import { isElementaryPage } from 'components/Common/LayoutV2/utils';
import LoadingComponent from 'components/Common/SectionPreloader';

import FeatureOnboarding from 'containers/FeatureOnboarding';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import useLocalStorage from 'hooks/useLocalStorage';
import useRedirect from 'hooks/useRedirect';
import useSwitchUserMode from 'hooks/useSwitchUserMode';
import useUserRedux from 'hooks/useUserRedux';

import authRoutes from 'routes/AuthRoutes';
import counselorRoutes from 'routes/CounselorRoutes';
import devRoutes from 'routes/DevRoutes';
import useMatchRoutes from 'routes/hooks/useMatchRoutes';
import useStudentDemoCheck from 'routes/hooks/useStudentDemoCheck';
import memberRoutes from 'routes/MemberRoutes';
import publicRoutes from 'routes/PublicRoutes';

import studentRoutes from 'routes/StudentRoutes';

import { DASHBOARD_MODES } from 'modules/AccountSetupV2/widgets/K12DashboardSwitcher/constants';

import MemberOrNonLoggedUserEventListRedirect from 'modules/EventSchedulerV2/Member/components/MemberOrNonLoggedUserEventListRedirect';
import LoginRedirect from 'modules/LoginRedirect';

const ClaimStudent = lazy(
  () => import('../modules/Guardians/pages/ClaimStudent'),
);

const PublicEventSchedulerRoutes = lazy(
  () => import('modules/EventSchedulerV2/Public'),
);

const OnboardingAndLogin = lazy(
  () => import('modules/OnboardingAndLogin/routes'),
);

const NotAuthorized = lazy(() => import('components/Common/Error403'));

const RoutesWrapper = () => {
  const {
    isMember,
    isStudent,
    isGuardian,
    isAlumni,
    isLoggedIn,
    isCounselor,
    isCounselorAndGuardian,
    isOAuth2Authenticated,
    data: { type, currentUserMode },
  } = useUserRedux();
  const { matchRoutes } = useMatchRoutes();
  const { switchUserMode } = useSwitchUserMode();
  const { redirect } = useRedirect();
  const [loginRedirectURL] = useLocalStorage('loginRedirectURL');

  // Checks if we were referred by another tab, and we were currently demoing as a student,
  // if so continues demoing student
  useStudentDemoCheck();

  useGoogleAnalytics();

  const nonLoggedInOrMember =
    (!isLoggedIn || isMember) && !isCounselorAndGuardian;
  const isMemberOrGuardian = isMember || isGuardian;

  const handleUserModeSwitch = async () => {
    const isCounselorRouteMatches = matchRoutes(counselorRoutes());
    const isMemberRouteMatches = matchRoutes(memberRoutes({ isAlumni }));

    if (isCounselor && !isCounselorRouteMatches && isMemberRouteMatches) {
      return switchUserMode({ currentUserMode: 'guardian', reload: true });
    }
    if (
      (isMember || isGuardian) &&
      isCounselorRouteMatches &&
      !isMemberRouteMatches
    ) {
      if (isElementaryPage(window.location.pathname)) {
        return switchUserMode({ currentUserMode: 'elementary', reload: true });
      }
      return switchUserMode({ currentUserMode: 'secondary', reload: true });
    }
    if (!loginRedirectURL) {
      return redirect({
        to: DASHBOARD_MODES[currentUserMode].route,
        component: '*',
        reason:
          'This is the fallback for users who are guardian and admin at the same time.',
      });
    }
    return null;
  };

  return (
    <>
      <Suspense fallback={<LoadingComponent />}>
        <LoginRedirect />
        <Routes>
          {publicRoutes({ type, isLoggedIn })}
          {TEST_PAGE_ENABLED ? devRoutes() : []}
          {authRoutes()}
          {nonLoggedInOrMember && (
            <>
              <Route
                path="/event-scheduler/org/:districtId/redirect/"
                element={<MemberOrNonLoggedUserEventListRedirect />}
                key="organization-events"
              />
              <Route
                path="/event-scheduler/org/:districtId/:schoolId/redirect/"
                element={<MemberOrNonLoggedUserEventListRedirect />}
                key="organization-events"
              />
              <Route
                path="/events/*"
                element={<PublicEventSchedulerRoutes />}
              />
            </>
          )}
          {isStudent && studentRoutes()}
          {isCounselor && counselorRoutes()}
          {isMemberOrGuardian && memberRoutes({ isAlumni })}
          <Route path="/claim-student" element={<ClaimStudent />} />
          <Route
            path="/not-authorized"
            element={
              <NotAuthorized
                message="You are not authorized to view this page."
                goBack={false}
                hideErrorStatus
              />
            }
          />
          <Route
            path="*"
            element={
              <OnboardingAndLogin handleUserModeSwitch={handleUserModeSwitch} />
            }
          />
        </Routes>
      </Suspense>
      {!isOAuth2Authenticated && (
        <FeatureOnboarding currentLocation={window.location.pathname} />
      )}
    </>
  );
};

export default RoutesWrapper;
